/**
 * Microphone Test Screen
 *
 * Diagnostic screen to verify microphone and Azure Speech pipeline.
 * Accessible from ProfileScreen > Learning Preferences.
 * Runs a pronunciation assessment against a known test phrase and
 * interprets the result as a pass/fail mic health check.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Animated,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Box, VStack, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useThemeColors, FS_SM, FS_MD, FS_LG, FS_2XL } from '../theme';
import { AzureSpeech } from '../modules/AzureSpeech';
import type { PronunciationResult, CancellableAssessment } from '../modules/AzureSpeech';
import { getSpeechRelayConnection } from '../services/speechRelayAuth';
import { getActiveLocale } from '../types/lesson';
import type { LessonLanguage, SourceLocale, TargetLocale } from '../types/locale';
import {
  requestMicrophonePermission,
  showPermissionDeniedAlert,
  showPermissionBlockedAlert,
  showMicrophoneUnavailableAlert,
} from '../utils/permissions';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../store/userPreferencesSelectors';
import { useCourseStore } from '../store/courseStore';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { createLogger } from '../utils/logger';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

const log = createLogger('MicrophoneTest');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MicTestState = 'idle' | 'preparing' | 'recording' | 'processing' | 'completed' | 'error';
type MicStatus = 'working' | 'quality_issues' | 'not_detected';

interface MicTestResult {
  status: MicStatus;
  score: number | null;
  recognizedText?: string;
}

// ---------------------------------------------------------------------------
// Test phrase configuration — reads from locale files (native.testPhrase)
// ---------------------------------------------------------------------------

function getTestConfig(
  lessonLanguage: LessonLanguage,
  sourceLocale: SourceLocale,
  targetLocale: TargetLocale,
): { phrase: string; language: string } {
  const locale = getActiveLocale(lessonLanguage, sourceLocale, targetLocale);
  const localeMeta = useCourseStore.getState().locales.find((l) => l.code === locale)!;
  const azure = localeMeta.speech.azure;
  return { phrase: localeMeta.native.testPhrase, language: azure?.sttLocale ?? locale };
}

// ---------------------------------------------------------------------------
// Result interpretation
// ---------------------------------------------------------------------------

function interpretResult(result: PronunciationResult): MicTestResult {
  const score = result.pronunciationScore;
  if (score >= 40) {
    return { status: 'working', score, recognizedText: result.recognizedText };
  }
  if (score > 0) {
    return { status: 'quality_issues', score, recognizedText: result.recognizedText };
  }
  return { status: 'not_detected', score: null };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_RECORD_SIZE = 140;

const STATUS_COLORS = {
  working: '#27AE60',
  quality_issues: '#F39C12',
  not_detected: '#E74C3C',
} as const;

const STATUS_ICONS = {
  working: '\u2705',
  quality_issues: '\u26A0\uFE0F',
  not_detected: '\u274C',
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MicrophoneTestScreen() {
  const { t } = useTranslation();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  const testConfig = getTestConfig(lessonLanguage, sourceLocale, targetLocale);

  const [state, setState] = useState<MicTestState>('idle');
  const [result, setResult] = useState<MicTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/refs -- Animated.Value is a stable mutable object, reading .current during render is the standard RN pattern
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const assessmentRef = useRef<CancellableAssessment | null>(null);

  // Pulse animation during recording
  useEffect(() => {
    if (state === 'recording') {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 1.15, duration: 800, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 1, duration: 800, useNativeDriver: true }),
        ])
      ).start();
    } else {
      pulseAnim.setValue(1);
    }
  }, [state, pulseAnim]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      assessmentRef.current?.cancel();
      assessmentRef.current = null;
    };
  }, []);

  const handleStartTest = useCallback(async () => {
    // 1. Mic permission
    const permStatus = await requestMicrophonePermission();
    if (permStatus === 'denied') { showPermissionDeniedAlert(); return; }
    if (permStatus === 'blocked') { showPermissionBlockedAlert(); return; }
    if (permStatus === 'unavailable') { showMicrophoneUnavailableAlert(); return; }

    // 2. Reset state
    setState('preparing');
    setResult(null);
    setError(null);

    try {
      // 3. Fetch relay connection (host + Supabase JWT)
      log.info('Fetching speech relay connection for mic test');
      const connection = await getSpeechRelayConnection();

      // 4. Start assessment — promise returns the result directly,
      //    no event subscription needed for this simple test flow
      log.info('Starting mic test assessment', { language: testConfig.language });
      setState('recording');

      const assessment = AzureSpeech.assessPronunciationWithCancel({
        host: connection.host,
        token: connection.token,
        referenceText: testConfig.phrase,
        language: testConfig.language,
        phraseList: ['Fluencia'],
      });
      assessmentRef.current = assessment;

      const assessmentResult = await assessment.promise;
      assessmentRef.current = null;

      // Cancelled by user — already set to idle in handleButtonPress
      if (!assessmentResult) return;

      // 5. Interpret result
      log.info('Mic test completed', { score: assessmentResult.pronunciationScore });
      const interpreted = interpretResult(assessmentResult);
      setResult(interpreted);
      setState('completed');
    } catch (err: unknown) {
      log.error('Mic test failed', err);
      setState('error');
      setError(t('micTest.errorConnection'));
    }
  }, [testConfig, t]);

  const handleButtonPress = useCallback(() => {
    if (state === 'recording') {
      assessmentRef.current?.cancel();
      assessmentRef.current = null;
      setState('idle');
    } else if (state === 'idle' || state === 'completed' || state === 'error') {
      handleStartTest();
    }
  }, [state, handleStartTest]);

  const isLoading = state === 'preparing' || state === 'processing';
  const recordSize = Math.round(BASE_RECORD_SIZE * fontScale);
  const buttonText = useButtonText(state);

  return (
    <ScrollView
      testID="mic-test-screen"
      style={{ flex: 1, backgroundColor: theme.background }}
      contentContainerStyle={{ padding: 20, paddingBottom: 8 + bottomInset }}
    >
      <ScreenHeader title={t('micTest.title')} />
      {/* Instructions */}
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, marginBottom: 24, lineHeight: FS_MD * fontScale * 1.5, ...ffs }}
      >
        {t('micTest.instructions')}
      </Text>

      {/* Test phrase */}
      <VStack space="xs" mb="$6">
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
        >
          {t('micTest.sayThisPhrase')}
        </Text>
        <Box
          testID="mic-test-text-phrase"
          style={{ backgroundColor: theme.inputBg }}
          p="$5"
          rounded="$xl"
        >
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_2XL * fontScale, fontWeight: 'bold', color: theme.text, textAlign: 'center', ...ffs }}
          >
            {testConfig.phrase}
          </Text>
        </Box>
      </VStack>

      {/* Record button */}
      <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
        <Box alignItems="center" my="$6">
          <TouchableOpacity
            testID="mic-test-button-record"
            accessibilityLabel={buttonText}
            accessibilityRole="button"
            onPress={handleButtonPress}
            disabled={isLoading}
            activeOpacity={0.7}
            style={{
              width: recordSize,
              height: recordSize,
              borderRadius: recordSize / 2,
              backgroundColor: state === 'recording' ? theme.recording : theme.primary,
              opacity: isLoading ? 0.6 : 1,
              justifyContent: 'center',
              alignItems: 'center',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
            }}
          >
            {isLoading ? (
              <ActivityIndicator color="white" size="large" />
            ) : (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: 'white', textAlign: 'center', paddingHorizontal: 16, ...ffs }}
              >
                {buttonText}
              </Text>
            )}
          </TouchableOpacity>
        </Box>
      </Animated.View>

      {/* Result section */}
      {state === 'completed' && result && (
        <ResultSection result={result} theme={theme} fontScale={fontScale} ffs={ffs} />
      )}

      {/* Error section */}
      {state === 'error' && error && (
        <Box style={{ backgroundColor: theme.errorBg }} p="$4" rounded="$xl">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, color: theme.error, textAlign: 'center', ...ffs }}
          >
            {error}
          </Text>
        </Box>
      )}
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// Button helpers
// ---------------------------------------------------------------------------

function useButtonText(state: MicTestState): string {
  const { t } = useTranslation();
  switch (state) {
    case 'recording': return t('micTest.recording');
    case 'completed':
    case 'error': return t('micTest.testAgain');
    default: return t('micTest.startTest');
  }
}

// ---------------------------------------------------------------------------
// Result display
// ---------------------------------------------------------------------------

interface ResultSectionProps {
  result: MicTestResult;
  theme: ReturnType<typeof useThemeColors>;
  fontScale: number;
  ffs: { fontFamily: string } | undefined;
}

const ResultSection = React.memo(function ResultSection({ result, theme, fontScale, ffs }: ResultSectionProps) {
  const { t } = useTranslation();
  const statusColor = STATUS_COLORS[result.status];
  const statusIcon = STATUS_ICONS[result.status];

  return (
    <Box
      testID="mic-test-result-status"
      style={{ backgroundColor: theme.card, borderWidth: 2, borderColor: statusColor }}
      p="$5"
      rounded="$2xl"
    >
      {/* Status header */}
      <HStack space="sm" alignItems="center" mb="$3">
        <Text allowFontScaling={false} style={{ fontSize: 28 }}>{statusIcon}</Text>
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_LG * fontScale, fontWeight: 'bold', color: statusColor, flex: 1, ...ffs }}
        >
          {result.status === 'working' && t('micTest.resultWorking')}
          {result.status === 'quality_issues' && t('micTest.resultQuality')}
          {result.status === 'not_detected' && t('micTest.resultNotDetected')}
        </Text>
      </HStack>

      {/* Working: detail text + recognized */}
      {result.status === 'working' && (
        <VStack space="sm">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('micTest.resultWorkingDetail')}
          </Text>
          {result.recognizedText && (
            <Box style={{ backgroundColor: theme.inputBg }} p="$3" rounded="$lg" mt="$2">
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, marginBottom: 4, ...ffs }}
              >
                {t('micTest.youSaid')}
              </Text>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_MD * fontScale, color: theme.text, fontStyle: 'italic', ...ffs }}
              >
                {result.recognizedText}
              </Text>
            </Box>
          )}
        </VStack>
      )}

      {/* Quality issues: tips */}
      {result.status === 'quality_issues' && (
        <TipsList
          tips={[
            t('micTest.resultQualityTip1'),
            t('micTest.resultQualityTip2'),
            t('micTest.resultQualityTip3'),
          ]}
          theme={theme}
          fontScale={fontScale}
          ffs={ffs}
        />
      )}

      {/* Not detected: tips */}
      {result.status === 'not_detected' && (
        <TipsList
          tips={[
            t('micTest.resultNotDetectedTip1'),
            t('micTest.resultNotDetectedTip2'),
            t('micTest.resultNotDetectedTip3'),
            t('micTest.resultNotDetectedTip4'),
          ]}
          theme={theme}
          fontScale={fontScale}
          ffs={ffs}
        />
      )}
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Tips list
// ---------------------------------------------------------------------------

interface TipsListProps {
  tips: string[];
  theme: ReturnType<typeof useThemeColors>;
  fontScale: number;
  ffs: { fontFamily: string } | undefined;
}

const TipsList = React.memo(function TipsList({ tips, theme, fontScale, ffs }: TipsListProps) {
  const { t } = useTranslation();
  return (
    <VStack space="sm" mt="$2">
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
      >
        {t('micTest.tipsTitle')}
      </Text>
      {tips.map((tip, i) => (
        <HStack key={i} space="sm" alignItems="flex-start">
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary }}>
            {'\u2022'}
          </Text>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_SM * fontScale, color: theme.text, flex: 1, ...ffs }}
          >
            {tip}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
});
